import React from 'react';
import EventEmitter from 'events';

import util from '../tools/util';
import UserStore from './user_store';

export default {
  once,
  addChangeListener,
  removeChangeListener,
  getOrg,
  useOrg,
  init,
  fetch,
  createOrg,
  leaveOrg,
  updateOrg,
  addBucket,
  orderBuckets,
  updateBucket,
  updateBucketStatus,
};

const g_orgMap = new Map<number, any>();

const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function _subscribe(callback: any) {
  addChangeListener(callback);
  return () => {
    removeChangeListener(callback);
  };
}
function init(done) {
  done?.();
}
function getOrg(org_id: number) {
  return g_orgMap.get(org_id);
}
export function useOrg(org_id: number) {
  const _get = React.useCallback(() => {
    return getOrg(org_id);
  }, [org_id]);
  return React.useSyncExternalStore(_subscribe, _get);
}
function fetch(org_id: number, done?: (err: any) => void) {
  const opts = { url: `/api/1/org/${org_id}` };
  UserStore.get(opts, (err, body) => {
    if (err) {
      util.errorLog('OrgStore.fetch: err:', err, body);
    } else {
      const old = g_orgMap.get(org_id);
      if (!util.deepEqual(old, body)) {
        g_orgMap.set(org_id, body);
        _emit('update');
      }
    }
    done?.(err);
  });
}
function createOrg(params: any, done: (err: any, org_id?: any) => void) {
  const { org_name } = params;

  const opts = {
    url: `/api/1/org`,
    body: {
      org_name,
    },
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('OrgStore.createOrg: post err:', err, body);
      done(err);
    } else {
      UserStore.verifySessionKey({}, () => done(null, body?.org_id));
    }
  });
}
function leaveOrg(params: any, done: (err?: any) => void) {
  const { org_id } = params;

  const opts = {
    url: `/api/1/org/${org_id}/leave`,
  };
  UserStore.del(opts, (err, body) => {
    if (err) {
      util.errorLog('OrgStore.leaveOrg: post err:', err, body);
      done(err);
    } else {
      UserStore.verifySessionKey({}, () => done());
    }
  });
}
function updateOrg(params: any, done: (err?: any) => void) {
  const { org_id, ...other } = params;
  const opts = {
    url: `/api/1/org/${org_id}`,
    body: other,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('OrgStore.updateOrg: post err:', err, body);
      done(err);
    } else {
      fetch(org_id, () => done());
      UserStore.verifySessionKey({}, () => {});
    }
  });
}
function addBucket(params: any, done: (err?: any) => void) {
  const { org_id, ...other } = params;
  const opts = {
    url: `/api/1/org/${org_id}/s3_bucket`,
    body: other,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('OrgStore.addBucket: post err:', err, body);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
function orderBuckets(params: any, done: (err?: any) => void) {
  const { org_id, ...other } = params;
  const opts = {
    url: `/api/1/org/${org_id}/s3_bucket_order`,
    body: other,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('OrgStore.orderBuckets: post err:', err, body);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
function updateBucket(params: any, done: (err?: any) => void) {
  const { org_id, s3_bucket_id, ...other } = params;
  const opts = {
    url: `/api/1/org/${org_id}/s3_bucket/${s3_bucket_id}`,
    body: other,
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('OrgStore.updateBucket: post err:', err, body);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
function updateBucketStatus(params: any, done: (err?: any) => void) {
  const { org_id, s3_bucket_id, is_disabled } = params;
  const opts = {
    url: `/api/1/org/${org_id}/s3_bucket/${s3_bucket_id}/status`,
    body: { is_disabled },
  };
  UserStore.post(opts, (err, body) => {
    if (err) {
      util.errorLog('OrgStore.updateBucketStatus: post err:', err, body);
      done(err);
    } else {
      fetch(org_id, () => done());
    }
  });
}
